import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import "./EmployeeMultiSelect.css";
import { FaAngleDown } from "react-icons/fa";
const EmployeeMultiSelect = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select Employees...",
  loading = false,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm("");
    }
  }, [isOpen]);

  // Memoized filtered options
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Selection logic
  const handleSelect = (id) => {
    if (selectedValues.includes(id)) {
      onChange(selectedValues.filter((i) => i !== id));
    } else {
      onChange([...selectedValues, id]);
    }
  };

  // Clear all
  const handleClearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  // UI text
  const selectedCount = selectedValues.length;
  const buttonText =
    selectedCount > 0
      ? `${selectedCount} employee${selectedCount > 1 ? "s" : ""} selected`
      : placeholder;

  return (
    <div
      className={`employee-multi-select position-relative ${className}`}
      ref={dropdownRef}
      tabIndex={0}
    >
      <button
        type="button"
        className={`employee-multi-select__trigger ${isOpen ? "is-open" : ""}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="employee-multi-select__trigger-text">{buttonText}</span>
        <span className="employee-multi-select__trigger-icon" aria-hidden="true">
          <FaAngleDown />
        </span>
      </button>
      {isOpen && (
        <div className="employee-multi-select__menu" role="listbox" aria-multiselectable="true">
          <div className="employee-multi-select__toolbar">
            <input
              type="text"
              className="form-control form-control-sm employee-multi-select__search"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <button
              type="button"
              className={`employee-multi-select__clear ${selectedCount === 0 ? "is-hidden" : ""}`}
              onClick={handleClearAll}
              tabIndex={selectedCount === 0 ? -1 : 0}
              disabled={selectedCount === 0}
            >
              Clear All
            </button>
          </div>
          {loading ? (
            <div className="employee-multi-select__state">Loading...</div>
          ) : options.length === 0 ? (
            <div className="employee-multi-select__state">No employees available</div>
          ) : filteredOptions.length === 0 ? (
            <div className="employee-multi-select__state">
              No employees found for "{searchTerm}"
            </div>
          ) : (
            <ul className="employee-multi-select__list">
              {filteredOptions.map((opt) => (
                <li
                  key={opt.value}
                  className={`employee-multi-select__option ${
                    selectedValues.includes(opt.value) ? "is-selected" : ""
                  }`}
                >
                  <label className="employee-multi-select__option-label">
                    <input
                      type="checkbox"
                      className="employee-multi-select__checkbox"
                      checked={selectedValues.includes(opt.value)}
                      onChange={() => handleSelect(opt.value)}
                    />
                    <span className="employee-multi-select__option-text">{opt.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

EmployeeMultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default EmployeeMultiSelect;
